'use client';
import { useState, useCallback, useRef } from 'react';
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_IMAGES_ONLY,
  MAX_FILE_SIZE,
  MAX_VIDEO_SIZE,
  ALLOWED_VIDEO_TYPES,
} from './constants';
import { IFile, IFileMeta, T_FILE_MIME_ENUM_TYPE } from './types';
import { generateUniqueId } from '@ui/constants';
import { useToast } from '@ui/components';
import { sanitizeFilename } from '@ui/lib/utils';

interface Props {
  maxFiles?: number;
  allowVideos?: boolean;
  /** When true, use creator copy: "You can upload up to 10 files total. X file(s) were not added." */
  maxFilesErrorCreatorStyle?: boolean;
}

export function useFiles({
  maxFiles = 10,
  allowVideos = false,
  maxFilesErrorCreatorStyle = false,
}: Props = {}) {
  const toast = useToast();
  const [files, setFiles] = useState<IFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const createdObjectUrlsRef = useRef<Set<string>>(new Set());

  const isBlobUrl = (url: string | undefined) => !!url && url.startsWith('blob:');

  const readVideoDuration = (url: string) =>
    new Promise<number>((resolve, reject) => {
      const videoEl = document.createElement('video');
      videoEl.preload = 'metadata';
      videoEl.src = url;
      const onLoaded = () => {
        resolve(videoEl.duration || 0);
        cleanup();
      };
      const onError = () => {
        reject(new Error('Failed to read video metadata'));
        cleanup();
      };
      const cleanup = () => {
        videoEl.removeEventListener('loadedmetadata', onLoaded);
        videoEl.removeEventListener('error', onError);
      };
      videoEl.addEventListener('loadedmetadata', onLoaded);
      videoEl.addEventListener('error', onError);
    });

  const processFileMeta = useCallback(
    (fileMeta: IFileMeta): IFile | null => {
      const allowedTypes = allowVideos ? ALLOWED_FILE_TYPES : ALLOWED_FILE_TYPES_IMAGES_ONLY;
      const acceptedFormats = allowVideos
        ? 'PNG, JPG, JPEG, GIF, MP4, MOV, WebM'
        : 'PNG, JPG, JPEG, GIF';

      if (!allowedTypes.includes(fileMeta.mime_type as any)) {
        setError(`Unsupported file type. Acceptable formats: ${acceptedFormats}.`);
        return null;
      }

      const isVideo = ALLOWED_VIDEO_TYPES.includes(fileMeta.mime_type as any);

      // If video is not allowed but user tried to upload one
      if (isVideo && !allowVideos) {
        setError(`Videos are not allowed in this context. Acceptable formats: ${acceptedFormats}.`);
        return null;
      }

      const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
      const sizeLabel = isVideo ? '100MB' : '10MB';

      if (fileMeta.size > maxSize) {
        const sizeStr = `${(fileMeta.size / 1024 / 1024).toFixed(1)} MB`;
        setError(
          `"${fileMeta.name}" (${sizeStr}) exceeds ${sizeLabel}. Please choose a smaller file.`,
        );
        return null;
      }

      // Front-end hint: videos must be 2 minutes or less
      if (isVideo) {
        // This displays a helpful message early; real enforcement happens in onSelectFiles
        // after we measure duration via metadata.
        setError(null);
      }

      return {
        src: fileMeta.source,
        file: null,
        id: generateUniqueId(),
        meta: fileMeta,
      };
    },
    [allowVideos],
  );

  const onSelectFiles = useCallback(
    async (fileList: FileList) => {
      const newFiles: IFile[] = [];
      const totalFiles = files.length + fileList.length;
      const rejectedOversized: { name: string; size: number; limitLabel: string }[] = [];

      for (const file of Array.from(fileList)) {
        const sanitizedFilename = sanitizeFilename(file.name);

        const sanitizedFile = new File([file], sanitizedFilename, {
          type: file.type,
          lastModified: file.lastModified,
        });

        const isVideo = ALLOWED_VIDEO_TYPES.includes(sanitizedFile.type as any);
        const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
        const sizeLabel = isVideo ? '100MB' : '10MB';

        // Check size before creating object URL so we don't leak blob URLs for rejected files
        if (sanitizedFile.size > maxSize) {
          rejectedOversized.push({
            name: sanitizedFile.name,
            size: sanitizedFile.size,
            limitLabel: sizeLabel,
          });
          continue;
        }

        const meta: IFileMeta = {
          mime_type: sanitizedFile.type as T_FILE_MIME_ENUM_TYPE,
          name: sanitizedFile.name,
          size: sanitizedFile.size,
          source: URL.createObjectURL(sanitizedFile),
        };
        createdObjectUrlsRef.current.add(meta.source);

        // If video, validate duration via metadata (<= 120s)
        if (isVideo) {
          try {
            const duration = await readVideoDuration(meta.source);

            if (duration > 90) {
              setError('Video must be 1 minute 30 seconds or less (90s).');
              continue; // skip this file
            }
          } catch {
            setError('Unable to verify video duration. Please try another file.');
            continue;
          }
        }

        const processedFile = processFileMeta(meta);
        if (processedFile) {
          newFiles.push({ ...processedFile, file: sanitizedFile });
        }

        if (files.length + newFiles.length >= maxFiles) {
          break;
        }
      }

      const notAddedCount = totalFiles - files.length - newFiles.length;
      const maxFilesError =
        totalFiles > maxFiles
          ? maxFilesErrorCreatorStyle
            ? `You can upload up to 10 files total. ${notAddedCount} file(s) were not added.`
            : `You can only add up to ${maxFiles} ${
                maxFiles === 1 ? 'file' : 'files'
              }. ${notAddedCount} file(s) from this selection were not added.`
          : null;

      if (rejectedOversized.length > 0) {
        const sizeStr = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;
        const list =
          rejectedOversized.length === 1
            ? `"${rejectedOversized[0].name}" (${sizeStr(rejectedOversized[0].size)})`
            : rejectedOversized.map((f) => `"${f.name}" (${sizeStr(f.size)})`).join(', ');
        const message =
          rejectedOversized.length === 1
            ? `${list} exceeds ${rejectedOversized[0].limitLabel}. Please choose a smaller file.`
            : `The following files exceed the size limit and weren't added: ${list}. Max size: ${rejectedOversized[0].limitLabel}.`;
        setError(maxFilesError ? `${maxFilesError} ${message}` : message);
        toast(
          {
            title:
              rejectedOversized.length === 1
                ? `"${rejectedOversized[0].name}" is over ${rejectedOversized[0].limitLabel}`
                : `${rejectedOversized.length} files over size limit weren't added`,
          },
          { type: 'warning' },
        );
      } else if (maxFilesError) {
        setError(maxFilesError);
        toast(
          {
            title: maxFilesErrorCreatorStyle
              ? 'Maximum 10 files allowed. Some were not added.'
              : `Maximum ${maxFiles} ${
                  maxFiles === 1 ? 'file' : 'files'
                } allowed. Some were not added.`,
          },
          { type: 'warning' },
        );
      }

      if (newFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...newFiles]);
        if (rejectedOversized.length === 0 && !maxFilesError) {
          setError(null);
        }
      }
    },
    [files, processFileMeta, toast, maxFiles, maxFilesErrorCreatorStyle],
  );

  const addInitialFiles = useCallback(
    (initialFiles: IFileMeta[]) => {
      const newFiles: IFile[] = [];

      for (const fileMeta of initialFiles) {
        const processedFile = processFileMeta(fileMeta);
        if (processedFile) {
          newFiles.push({ ...processedFile, preventUpload: true });
        }

        if (files.length + newFiles.length >= maxFiles) {
          break;
        }
      }

      if (newFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...newFiles]);
        setError(null);
      }
    },
    [files, processFileMeta, maxFiles],
  );

  const deleteFile = useCallback((id: string) => {
    setFiles((prevFiles) => {
      const target = prevFiles.find((f) => f.id === id);
      if (target && isBlobUrl(target.src) && createdObjectUrlsRef.current.has(target.src)) {
        URL.revokeObjectURL(target.src);
        createdObjectUrlsRef.current.delete(target.src);
      }
      return prevFiles.filter((file) => file.id !== id);
    });
    setError(null);
  }, []);

  /**
   * Clear selected files.
   * - Default behavior revokes created blob: URLs to avoid leaks.
   * - Pass `{ revoke: false }` to clear the UI without revoking blob URLs.
   *   This is useful when an optimistic message is still rendering those blob URLs.
   */
  const onDiscardFiles = useCallback((opts?: { revoke?: boolean }) => {
    const shouldRevoke = opts?.revoke !== false;
    setFiles([]);
    if (shouldRevoke) {
      createdObjectUrlsRef.current.forEach((url) => {
        if (isBlobUrl(url)) URL.revokeObjectURL(url);
      });
      createdObjectUrlsRef.current.clear();
    }
    setError(null);
  }, []);

  const reportMaxFilesExceeded = useCallback(() => {
    const message = maxFilesErrorCreatorStyle
      ? 'You can upload up to 10 files total. Remove one to add more.'
      : `You can only add up to ${maxFiles} ${
          maxFiles === 1 ? 'file' : 'files'
        }. Remove one to add more.`;
    setError(message);
  }, [maxFiles, maxFilesErrorCreatorStyle]);

  return {
    files,
    onSelectFiles,
    addInitialFiles,
    deleteFile,
    error,
    onDiscardFiles,
    reportMaxFilesExceeded,
  };
}
